/**
 * Admin Memberships API Service
 *
 * Provides access to membership ledger endpoints (Positions Monitor).
 * Gate: ADMIN_MEMBERSHIP_UI_ENABLED. Admin-only.
 */

import { apiClient } from "./apiClient";

export interface PositionListItem {
  admin_position_id: string;
  symbol: string;
  state: string;
  members_count: number;
  last_update: string | null;
  ttl_seconds: number | null;
  admin_entry_order_id: string | null;
  admin_exit_order_id: string | null;
}

export interface MembershipListResponse {
  positions: PositionListItem[];
  total: number;
}

export interface MembershipDetailResponse {
  admin_position_id: string;
  meta: Record<string, unknown>;
  members: string[];
  derived_stuck_users: Array<Record<string, unknown>>;
}

export interface ExtendTtlRequest {
  days: number;
  reason?: string;
}

export interface ExtendTtlResponse {
  success: boolean;
  message: string;
}

export interface CleanupRequest {
  dry_run?: boolean;
  delete?: boolean;
  reason?: string;
}

export interface CleanupResponse {
  dry_run: boolean;
  would_delete: boolean;
  deleted: boolean;
  message: string;
  current_counts?: { state?: string; members_count?: number };
}

export interface ForceExitSubscriberRequest {
  user_id: string;
  reason?: string;
  override_membership_check?: boolean;
}

export interface ForceExitSubscriberResponse {
  success: boolean;
  job_id?: string;
  message: string;
}

export interface RebuildMembershipRequest {
  dry_run?: boolean;
  reason?: string;
}

export interface RebuildMembershipResponse {
  dry_run: boolean;
  applied: boolean;
  candidates_count: number;
  added_count: number;
  message: string;
  candidates: string[];
}

export async function listPositions(params?: {
  state?: "open" | "recent";
  limit?: number;
}): Promise<MembershipListResponse> {
  const response = await apiClient.get<MembershipListResponse>(
    "/admin/positions/memberships",
    { params },
  );
  return response.data;
}

export async function getMembershipDetails(
  adminPositionId: string,
): Promise<MembershipDetailResponse> {
  const response = await apiClient.get<MembershipDetailResponse>(
    "/admin/positions/membership-details",
    { params: { admin_position_id: adminPositionId } },
  );
  return response.data;
}

export async function extendTtl(
  adminPositionId: string,
  body: ExtendTtlRequest,
): Promise<ExtendTtlResponse> {
  const response = await apiClient.post<ExtendTtlResponse>(
    "/admin/positions/extend-ttl",
    body,
    { params: { admin_position_id: adminPositionId } },
  );
  return response.data;
}

export async function cleanup(
  adminPositionId: string,
  body: CleanupRequest,
): Promise<CleanupResponse> {
  const response = await apiClient.post<CleanupResponse>(
    "/admin/positions/cleanup",
    body,
    { params: { admin_position_id: adminPositionId } },
  );
  return response.data;
}

export async function forceExitSubscriber(
  adminPositionId: string,
  body: ForceExitSubscriberRequest,
): Promise<ForceExitSubscriberResponse> {
  const response = await apiClient.post<ForceExitSubscriberResponse>(
    "/admin/positions/force-exit-subscriber",
    body,
    { params: { admin_position_id: adminPositionId } },
  );
  return response.data;
}

export async function rebuildMembership(
  adminPositionId: string,
  body: RebuildMembershipRequest,
): Promise<RebuildMembershipResponse> {
  const response = await apiClient.post<RebuildMembershipResponse>(
    "/admin/positions/rebuild-membership",
    body,
    { params: { admin_position_id: adminPositionId } },
  );
  return response.data;
}
